import { IDocumentService, IWindowService } from 'angular';

export interface IDimensions {
  width?: number;
  height?: number;
}

export interface IOffset {
  top?: number;
  left?: number;
}

export class DomService {
  static $inject = [
    '$window',
    '$document'
  ];
  zIndex: number = 1000;

  private calculatedScrollbarWidth: number = null;

  private browser: any = {};

  constructor( private $window: IWindowService,
               private $document: IDocumentService ) {

  }

  relativePosition( element: any, target: any ) {
    const elementDimensions: IDimensions = element.offsetParent ? {
      width: element.offsetWidth,
      height: element.offsetHeight
    } : this.getHiddenElementDimensions(element);
    const targetHeight = target.offsetHeight;
    const targetWidth = target.offsetWidth;
    const targetOffset = target.getBoundingClientRect();
    const windowScrollTop = this.getWindowScrollTop();
    const viewport = this.getViewport();
    let top;
    let left;

    if ( (targetOffset.top + targetHeight + elementDimensions.height) > viewport.height ) {
      top = -1 * (elementDimensions.height);
      if ( targetOffset.top + top < 0 ) {
        top = 0;
      }
    } else {
      top = targetHeight;
    }


    if ( (targetOffset.left + elementDimensions.width) > viewport.width ) {
      left = targetWidth - elementDimensions.width;
    } else {
      left = 0;
    }
    element.style.top = `${top}px`;
    element.style.left = `${left}px`;
  }

  absolutePosition( element: any, target: any ): void {
    const elementDimensions: IDimensions = element.offsetParent ? {
      width: element.offsetWidth,
      height: element.offsetHeight
    } : this.getHiddenElementDimensions(element);
    const elementOuterHeight = elementDimensions.height;
    const elementOuterWidth = elementDimensions.width;
    const targetOuterHeight = target.offsetHeight;
    const targetOuterWidth = target.offsetWidth;
    const targetOffset = target.getBoundingClientRect();
    const windowScrollTop = this.getWindowScrollTop();
    const windowScrollLeft = this.getWindowScrollLeft();
    const viewport: IDimensions = this.getViewport();
    let top;
    let left;

    if ( targetOffset.top + targetOuterHeight + elementOuterHeight > viewport.height ) {
      top = targetOffset.top + windowScrollTop - elementOuterHeight;
      if ( top < 0 ) {
        top = windowScrollTop;
      }
    } else {
      top = targetOuterHeight + targetOffset.top + windowScrollTop;
    }

    if ( targetOffset.left + targetOuterWidth + elementOuterWidth > viewport.width ) {
      left = targetOffset.left + windowScrollLeft + targetOuterWidth - elementOuterWidth;
    } else {
      left = targetOffset.left + windowScrollLeft;
    }

    element.style.top = `${top}px`;
    element.style.left = `${left}px`;
  }

  getHiddenElementOuterHeight( element: any ): number {
    element.style.visibility = 'hidden';
    element.style.display = 'block';
    const elementHeight = element.offsetHeight;
    element.style.display = 'none';
    element.style.visibility = 'visible';

    return elementHeight;
  }

  getHiddenElementOuterWidth( element: any ): number {
    element.style.visibility = 'hidden';
    element.style.display = 'block';
    const elementWidth = element.offsetWidth;
    element.style.display = 'none';
    element.style.visibility = 'visible';

    return elementWidth;
  }

  getHiddenElementDimensions( element: any ): IDimensions {
    const dimensions: IDimensions = {};
    element.style.visibility = 'hidden';
    element.style.display = 'block';
    dimensions.width = element.offsetWidth;
    dimensions.height = element.offsetHeight;
    element.style.display = 'none';
    element.style.visibility = 'visible';

    return dimensions;
  }

  scrollInView( container, item ) {
    const borderTopValue: string = getComputedStyle(container).getPropertyValue('borderTopWidth');
    const borderTop: number = borderTopValue ? parseFloat(borderTopValue) : 0;
    const paddingTopValue: string = getComputedStyle(container).getPropertyValue('paddingTop');
    const paddingTop: number = paddingTopValue ? parseFloat(paddingTopValue) : 0;
    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const offset = (itemRect.top + document.body.scrollTop) - (containerRect.top + document.body.scrollTop) - borderTop - paddingTop;
    const scroll = container.scrollTop;
    const elementHeight = container.clientHeight;
    const itemHeight = this.getOuterHeight(item);

    if ( offset < 0 ) {
      container.scrollTop = scroll + offset;
    } else if ( (offset + itemHeight) > elementHeight ) {
      container.scrollTop = scroll + offset - elementHeight + itemHeight;
    }
  }

  fadeIn( element, duration: number ) {
    element.style.opacity = 0;

    let last = +new Date();
    let opacity = 0;
    const tick = () => {
      opacity = +element.style.opacity.replace(',', '.') + (new Date().getTime() - last) / duration;
      element.style.opacity = opacity;
      last = +new Date();

      if ( +opacity < 1 ) {
        if ( this.$window.requestAnimationFrame ) {
          this.$window.requestAnimationFrame(tick);
        } else {
          setTimeout(tick, 16);
        }
      }
    };

    tick();
  }

  fadeOut( element, duration: number ) {
    let opacity = 1;
    const interval = 50;
    const gap = interval / duration;

    const fading = setInterval(() => {
      opacity = opacity - gap;

      if ( opacity <= 0 ) {
        opacity = 0;
        clearInterval(fading);
      }

      element.style.opacity = opacity;
    }, interval);
  }

  getWindowScrollTop(): number {
    const doc = document.documentElement;
    return (this.$window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
  }

  getWindowScrollLeft(): number {
    const doc = document.documentElement;
    return (this.$window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
  }

  matches( element, selector: string ): boolean {
    const p = Element.prototype;
    const func = ( s ) => {
      return [].indexOf.call(document.querySelectorAll(s), this) !== -1;
    };
    const f = p[ 'matches' ] || p.webkitMatchesSelector || p[ 'mozMatchesSelector' ] || p.msMatchesSelector || func;
    return f.call(element, selector);
  }

  getOuterWidth( el, margin? ): number {
    let width = el.offsetWidth;

    if ( margin ) {
      const style = getComputedStyle(el);
      width += parseFloat(style.marginLeft) + parseFloat(style.marginRight);
    }

    return width;
  }

  getHorizontalPadding( el ): number {
    const style = getComputedStyle(el);
    return parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
  }

  getHorizontalMargin( el ): number {
    const style = getComputedStyle(el);
    return parseFloat(style.marginLeft) + parseFloat(style.marginRight);
  }

  innerWidth( el ): number {
    let width = el.offsetWidth;
    const style = getComputedStyle(el);

    width += parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    return width;
  }

  width( el ): number {
    let width = el.offsetWidth;
    const style = getComputedStyle(el);

    width -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    return width;
  }

  getInnerHeight( el ): number {
    let height = el.offsetHeight;
    const style = getComputedStyle(el);

    height += parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    return height;
  }

  getOuterHeight( el, margin? ): number {
    let height = el.offsetHeight;

    if ( margin ) {
      const style = getComputedStyle(el);
      height += parseFloat(style.marginTop) + parseFloat(style.marginBottom);
    }

    return height;
  }

  getHeight( el ): number {
    let height = el.offsetHeight;
    const style = getComputedStyle(el);

    height -= parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);

    return height;
  }

  getWidth( el ): number {
    let width = el.offsetWidth;
    const style = getComputedStyle(el);

    width -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);

    return width;
  }

  getViewport(): IDimensions {
    const d = this.$document[ 0 ];
    const e = d.documentElement;
    const g = this.$document.find('body')[ 0 ];
    const width = this.$window.innerWidth || e.clientWidth || g.clientWidth;
    const height = this.$window.innerHeight || e.clientHeight || g.clientHeight;

    return { width, height };
  }

  getOffset( el ): IOffset {
    const rect = el.getBoundingClientRect();

    return {
      top: rect.top + document.body.scrollTop,
      left: rect.left + document.body.scrollLeft
    };
  }

  getUserAgent(): string {
    return navigator.userAgent;
  }

  isIE(): boolean {
    const ua = this.$window.navigator.userAgent;

    const msie = ua.indexOf('MSIE ');
    if ( msie > 0 ) {
      // IE 10 or older => return version number
      return true;
    }

    const trident = ua.indexOf('Trident/');
    if ( trident > 0 ) {
      // IE 11 => return version number
      const rv = ua.indexOf('rv:');
      return true;
    }

    const edge = ua.indexOf('Edge/');
    if ( edge > 0 ) {
      // Edge (IE 12+) => return version number
      return true;
    }

    // other browser
    return false;
  }

  appendChild( element: any, target: any ) {
    if ( this.isElement(target) ) {
      target.appendChild(element);
    } else if ( target.el && target.el.nativeElement ) {
      target.el.nativeElement.appendChild(element);
    } else {
      throw new Error(`Cannot append ${target} to ${element}`);
    }
  }

  removeChild( element: any, target: any ) {
    if ( this.isElement(target) ) {
      target.removeChild(element);
    } else if ( target.el && target.el.nativeElement ) {
      target.el.nativeElement.removeChild(element);
    } else {
      throw new Error('Cannot remove ' + element + ' from ' + target);
    }
  }

  isElement( obj: any ) {
    return (typeof HTMLElement === 'object' ? obj instanceof HTMLElement :
        obj && typeof obj === 'object' && obj !== null && obj.nodeType === 1 && typeof obj.nodeName === 'string'
    );
  }

  calculateScrollbarWidth(): number {
    if ( this.calculatedScrollbarWidth !== null ) {
      return this.calculatedScrollbarWidth;
    }

    const scrollDiv = document.createElement('div');
    scrollDiv.className = 'ui-scrollbar-measure';
    document.body.appendChild(scrollDiv);

    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);

    this.calculatedScrollbarWidth = scrollbarWidth;

    return scrollbarWidth;
  }

  invokeElementMethod( element: any, methodName: string, args?: any[] ): void {
    element[ methodName ].apply(element, args);
  }

  clearSelection() {
    if ( window.getSelection ) {
      if ( window.getSelection().empty ) {
        window.getSelection().empty();
      } else if ( window.getSelection().removeAllRanges && window.getSelection().rangeCount > 0 && window.getSelection().getRangeAt(0).getClientRects().length > 0 ) {
        window.getSelection().removeAllRanges();
      }
    } else if ( document[ 'selection' ] && document[ 'selection' ].empty ) {
      try {
        document[ 'selection' ].empty();
      } catch ( error ) {
        // ignore IE bug
      }
    }
  }

  getBrowser(): any {
    if ( !this.browser ) {
      const matched = this.resolveUserAgent();
      this.browser = {};

      if ( matched.browser ) {
        this.browser[ matched.browser ] = true;
        this.browser[ 'version' ] = matched.version;
      }

      if ( this.browser[ 'chrome' ] ) {
        this.browser[ 'webkit' ] = true;
      } else if ( this.browser[ 'webkit' ] ) {
        this.browser[ 'safari' ] = true;
      }
    }

    return this.browser;
  }

  resolveUserAgent(): any {
    const ua = navigator.userAgent.toLowerCase();
    const match = /(chrome)[ \/]([\w.]+)/.exec(ua) ||
      /(webkit)[ \/]([\w.]+)/.exec(ua) ||
      /(opera)(?:.*version|)[ \/]([\w.]+)/.exec(ua) ||
      /(msie) ([\w.]+)/.exec(ua) ||
      ua.indexOf('compatible') < 0 && /(mozilla)(?:.*? rv:([\w.]+)|)/.exec(ua) ||
      [];

    return {
      browser: match[ 1 ] || '',
      version: match[ 2 ] || '0'
    };
  }
}



// WEBPACK FOOTER //
// ./app/core/services/dom/dom.service.ts